import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, Code2, Copy, Plus, Trash2, Eye, ExternalLink,
  Settings, Globe, BedDouble, Loader2, Check, ChevronRight
} from "lucide-react";
import axios from "axios";
import { useAdminAuth, getAdminAuthHeaders } from "@/hooks/useAdminAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const PRODUCTION_URL = "https://csyncpro.com";
const makeEmbedUrl = (token) => `${PRODUCTION_URL}/embed/${token}`;
const makeIframeCode = (token) => `<iframe src="${makeEmbedUrl(token)}" width="100%" height="800" frameborder="0" style="border:none;min-height:800px;"></iframe>`;
const makePreviewUrl = (token) => `${SITE_URL}/embed/${token}`;
export default function EmbedManagement() {
  const navigate = useNavigate();
  const [embeds, setEmbeds] = useState([]);
  const [licenses, setLicenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCode, setShowCode] = useState(null);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    license_key: "",
    title: "",
    description: "",
    primary_color: "#2563eb",
    accent_color: "#f59e0b",
    show_phone: true,
    max_guests: 10,
    currency: "USD",
    payment_mode: "full",
    deposit_percentage: 100,
    cancellation_policy: "",
    tax_fees: []
  });
  const [showTaxConfig, setShowTaxConfig] = useState(false);

  useAdminAuth();

  const fetchEmbeds = async () => {
    try {
      const [embedRes, licenseRes] = await Promise.all([
        axios.get(`${API}/admin/embeds`, { headers: getAdminAuthHeaders() }),
        axios.get(`${API}/admin/licenses`, { headers: getAdminAuthHeaders() })
      ]);
      setEmbeds(embedRes.data.embeds || []);
      setLicenses((licenseRes.data.licenses || []).filter(l => l.cloudbeds_connected));
    } catch {
      toast.error("Failed to load embed data");
    }
    setLoading(false);
  };

  useEffect(() => { fetchEmbeds(); }, []);

  const handleCreate = async () => {
    if (!createForm.license_key) { toast.error("Select a license"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/admin/embed/generate`, createForm, {
        headers: getAdminAuthHeaders()
      });
      toast.success("Embed widget created!");
      setShowCreate(false);
      setShowCode({
        embed_url: makeEmbedUrl(res.data.embed_token),
        iframe_code: makeIframeCode(res.data.embed_token),
        embed_token: res.data.embed_token
      });
      fetchEmbeds();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create widget");
    }
    setCreating(false);
  };

  const handleDelete = async (token) => {
    if (!window.confirm("Delete this embed widget?")) return;
    try {
      await axios.delete(`${API}/admin/embed/${token}`, {
        headers: getAdminAuthHeaders()
      });
      toast.success("Widget deleted");
      fetchEmbeds();
    } catch {
      toast.error("Failed to delete widget");
    }
  };

  const toggleStatus = async (embed) => {
    const newStatus = embed.status === "active" ? "inactive" : "active";
    try {
      await axios.put(`${API}/admin/embed/${embed.embed_token}`, { status: newStatus }, {
        headers: getAdminAuthHeaders()
      });
      toast.success(`Widget ${newStatus === "active" ? "activated" : "deactivated"}`);
      fetchEmbeds();
    } catch {
      toast.error("Failed to update widget");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate("/admin")} data-testid="back-to-admin">
              <ArrowLeft className="w-4 h-4 mr-1" /> Admin
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Code2 className="w-5 h-5" /> Embed Widgets
              </h1>
              <p className="text-sm text-gray-500">Embeddable booking widgets for any website</p>
            </div>
          </div>
          <Button onClick={() => setShowCreate(true)} data-testid="create-embed-btn">
            <Plus className="w-4 h-4 mr-1" /> Create Widget
          </Button>
        </div>

        {/* Embeds Grid */}
        {embeds.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Globe className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <h3 className="font-semibold text-gray-700 mb-1">No Embed Widgets Yet</h3>
              <p className="text-gray-500 text-sm mb-4">Create a booking widget to embed on any website</p>
              <Button onClick={() => setShowCreate(true)} data-testid="create-first-embed-btn">
                <Plus className="w-4 h-4 mr-1" /> Create Your First Widget
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {embeds.map(embed => (
              <Card key={embed.embed_token} className="group relative" data-testid={`embed-card-${embed.embed_token}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <CardTitle className="text-sm font-semibold truncate">{embed.property_name}</CardTitle>
                      <p className="text-xs text-gray-400 mt-0.5 truncate">{embed.customer_email}</p>
                    </div>
                    <Badge variant={embed.status === "active" ? "default" : "secondary"} className="text-xs ml-2 shrink-0">
                      {embed.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-0 space-y-3">
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <BedDouble className="w-3.5 h-3.5" /> {embed.bookings_count || 0} bookings
                    </span>
                    <span>{new Date(embed.created_at).toLocaleDateString()}</span>
                  </div>

                  <div className="flex items-center gap-1">
                    <div className="w-5 h-5 rounded-full border" style={{ background: embed.settings?.primary_color || "#2563eb" }} />
                    <div className="w-5 h-5 rounded-full border" style={{ background: embed.settings?.accent_color || "#f59e0b" }} />
                    <span className="text-xs text-gray-400 ml-1">Theme</span>
                  </div>

                  <div className="flex gap-1.5 pt-1">
                    <Button
                      size="sm" variant="outline" className="flex-1 text-xs h-8"
                      onClick={() => setShowCode({
                        embed_url: makeEmbedUrl(embed.embed_token),
                        iframe_code: makeIframeCode(embed.embed_token),
                        embed_token: embed.embed_token
                      })}
                      data-testid={`get-code-${embed.embed_token}`}
                    >
                      <Code2 className="w-3 h-3 mr-1" /> Code
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => window.open(makePreviewUrl(embed.embed_token), "_blank")} data-testid={`preview-${embed.embed_token}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="outline" className="h-8" onClick={() => toggleStatus(embed)} data-testid={`toggle-${embed.embed_token}`}>
                      <Settings className="w-3 h-3" />
                    </Button>
                    <Button size="sm" variant="destructive" className="h-8" onClick={() => handleDelete(embed.embed_token)} data-testid={`delete-${embed.embed_token}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Create Embed Widget</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label className="text-sm">Property (License) *</Label>
              <select
                className="w-full mt-1 border border-gray-200 rounded-lg px-3 py-2 text-sm"
                value={createForm.license_key}
                onChange={e => setCreateForm(f => ({ ...f, license_key: e.target.value }))}
                data-testid="select-license"
              >
                <option value="">Select a Cloudbeds-connected license...</option>
                {licenses.map(l => (
                  <option key={l.license_key} value={l.license_key}>
                    {l.cloudbeds_hotel_name || l.license_key} — {l.customer_email}
                  </option>
                ))}
              </select>
              {licenses.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">No Cloudbeds-connected licenses found. Connect Cloudbeds first.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Widget Title</Label>
                <Input
                  value={createForm.title}
                  onChange={e => setCreateForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="Property name"
                  className="mt-1"
                  data-testid="widget-title"
                />
              </div>
              <div>
                <Label className="text-sm">Max Guests</Label>
                <Input
                  type="number"
                  value={createForm.max_guests}
                  onChange={e => setCreateForm(f => ({ ...f, max_guests: parseInt(e.target.value) || 10 }))}
                  className="mt-1"
                  data-testid="widget-max-guests"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm">Description</Label>
              <Input
                value={createForm.description}
                onChange={e => setCreateForm(f => ({ ...f, description: e.target.value }))}
                placeholder="Book your stay at..."
                className="mt-1"
                data-testid="widget-description"
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-sm">Primary Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={createForm.primary_color}
                    onChange={e => setCreateForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer border"
                    data-testid="primary-color"
                  />
                  <Input
                    value={createForm.primary_color}
                    onChange={e => setCreateForm(f => ({ ...f, primary_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm">Accent Color</Label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="color"
                    value={createForm.accent_color}
                    onChange={e => setCreateForm(f => ({ ...f, accent_color: e.target.value }))}
                    className="w-10 h-10 rounded cursor-pointer border"
                    data-testid="accent-color"
                  />
                  <Input
                    value={createForm.accent_color}
                    onChange={e => setCreateForm(f => ({ ...f, accent_color: e.target.value }))}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={createForm.show_phone}
                onChange={e => setCreateForm(f => ({ ...f, show_phone: e.target.checked }))}
                id="showPhone"
                data-testid="show-phone-toggle"
              />
              <label htmlFor="showPhone" className="text-sm text-gray-600">Show phone field on booking form</label>
            </div>

            {/* Tax & Fee Configuration */}
            <div className="border border-gray-200 rounded-lg p-3">
              <button type="button" onClick={() => setShowTaxConfig(!showTaxConfig)} className="flex items-center justify-between w-full text-sm font-semibold text-gray-700" data-testid="toggle-tax-config">
                Taxes & Fees Configuration
                <ChevronRight className={`w-4 h-4 transition-transform ${showTaxConfig ? "rotate-90" : ""}`} />
              </button>
              {showTaxConfig && (
                <div className="mt-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs">Currency</Label>
                      <select className="w-full mt-1 border border-gray-200 rounded px-2 py-1.5 text-sm" value={createForm.currency} onChange={e => setCreateForm(f => ({ ...f, currency: e.target.value }))} data-testid="currency-select">
                        <option value="USD">USD ($)</option>
                        <option value="BSD">BSD (B$)</option>
                        <option value="EUR">EUR</option>
                        <option value="GBP">GBP</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs">Payment Mode</Label>
                      <select className="w-full mt-1 border border-gray-200 rounded px-2 py-1.5 text-sm" value={createForm.payment_mode} onChange={e => setCreateForm(f => ({ ...f, payment_mode: e.target.value }))} data-testid="payment-mode-select">
                        <option value="full">Full Payment</option>
                        <option value="deposit">Deposit Only</option>
                      </select>
                    </div>
                  </div>
                  {createForm.payment_mode === "deposit" && (
                    <div>
                      <Label className="text-xs">Deposit Percentage</Label>
                      <Input type="number" min={10} max={100} value={createForm.deposit_percentage} onChange={e => setCreateForm(f => ({ ...f, deposit_percentage: parseFloat(e.target.value) || 100 }))} className="mt-1" data-testid="deposit-pct" />
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Cancellation Policy</Label>
                    <Input value={createForm.cancellation_policy} onChange={e => setCreateForm(f => ({ ...f, cancellation_policy: e.target.value }))} placeholder="Free cancellation up to 48 hours..." className="mt-1" data-testid="cancellation-policy" />
                  </div>

                  {/* Tax/Fee Lines */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-xs font-semibold">Tax & Fee Lines</Label>
                      <button type="button" onClick={() => setCreateForm(f => ({ ...f, tax_fees: [...f.tax_fees, { label: "", type: "percentage", amount: 0, enabled: true }] }))} className="text-xs text-blue-600 hover:underline" data-testid="add-tax-line">+ Add Line</button>
                    </div>
                    {createForm.tax_fees.map((tf, i) => (
                      <div key={i} className="flex items-center gap-2 mb-2">
                        <Input className="flex-1 text-xs" placeholder="VAT, Resort Fee..." value={tf.label} onChange={e => { const fees = [...createForm.tax_fees]; fees[i].label = e.target.value; setCreateForm(f => ({ ...f, tax_fees: fees })); }} data-testid={`tax-label-${i}`} />
                        <select className="border border-gray-200 rounded px-1.5 py-1.5 text-xs w-16" value={tf.type} onChange={e => { const fees = [...createForm.tax_fees]; fees[i].type = e.target.value; setCreateForm(f => ({ ...f, tax_fees: fees })); }}>
                          <option value="percentage">%</option>
                          <option value="flat">$</option>
                        </select>
                        <Input type="number" className="w-16 text-xs" value={tf.amount} onChange={e => { const fees = [...createForm.tax_fees]; fees[i].amount = parseFloat(e.target.value) || 0; setCreateForm(f => ({ ...f, tax_fees: fees })); }} data-testid={`tax-amount-${i}`} />
                        <button type="button" onClick={() => { const fees = createForm.tax_fees.filter((_, j) => j !== i); setCreateForm(f => ({ ...f, tax_fees: fees })); }} className="text-red-400 hover:text-red-600 text-lg">&times;</button>
                      </div>
                    ))}
                    {createForm.tax_fees.length === 0 && <p className="text-xs text-gray-400">No taxes or fees configured. Click "+ Add Line" to add VAT, resort fees, etc.</p>}
                  </div>
                </div>
              )}
            </div>

            <Button onClick={handleCreate} disabled={creating || !createForm.license_key} className="w-full" data-testid="create-widget-submit">
              {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              Generate Booking Engine
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Code Dialog */}
      <Dialog open={!!showCode} onOpenChange={() => setShowCode(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Code2 className="w-5 h-5" /> Embed Code</DialogTitle>
          </DialogHeader>
          {showCode && (
            <div className="space-y-4 pt-2">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-semibold">Option 1: iFrame Embed</Label>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(showCode.iframe_code)} data-testid="copy-iframe">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <pre className="bg-gray-900 text-green-400 rounded-lg p-3 text-xs overflow-x-auto whitespace-pre-wrap font-mono">
                  {showCode.iframe_code}
                </pre>
                <p className="text-xs text-gray-400 mt-1">Paste this into any HTML page to display the booking widget.</p>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-sm font-semibold">Option 2: Direct Link</Label>
                  <Button size="sm" variant="ghost" onClick={() => copyToClipboard(showCode.embed_url)} data-testid="copy-link">
                    <Copy className="w-3.5 h-3.5 mr-1" /> Copy
                  </Button>
                </div>
                <div className="bg-gray-100 rounded-lg p-3 text-sm break-all">
                  <a href={showCode.embed_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                    {showCode.embed_url} <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-amber-800 text-xs font-medium">Integration Tips:</p>
                <ul className="text-amber-700 text-xs mt-1 space-y-0.5 list-disc pl-4">
                  <li>Works on any website — WordPress, Squarespace, Wix, custom HTML</li>
                  <li>Responsive design adapts to container width</li>
                  <li>Bookings sync directly to Cloudbeds in real-time</li>
                  <li>All bookings appear in your CloudSync Pro ledger</li>
                </ul>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
